import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  isPushSupported,
  getPermissionStatus,
  subscribeToPush,
  getPushStatus,
} from '../utils/pushNotifications';
import { playNotificationSound } from '../utils/notificationSounds';
import { getInstallState, onInstallStateChange } from '../utils/pwa';

/**
 * EnablePushAlertsBanner
 *
 * One-time per device. Asks the seller to flip on push so they get a
 * cash-register sound on every new order (and softer pings for messages,
 * cancellations, low-stock).
 *
 * Visibility rules:
 *   - User is logged in as vendor (or admin)
 *   - Browser supports push (no-op on unsupported)
 *   - Permission is NOT already 'granted' AND not 'denied'
 *   - User hasn't dismissed in the last 14 days
 *   - iOS: only shown when running in standalone (push only works from home screen)
 */
const DISMISS_KEY = 'aniyor.push.dismissed_at';
const DISMISS_DAYS = 14;

export default function EnablePushAlertsBanner() {
  const { user, token } = useAuth() || {};
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!user || !token) { setShow(false); return; }
    if (!isPushSupported()) { setShow(false); return; }

    const perm = getPermissionStatus();
    if (perm === 'granted' || perm === 'denied') { setShow(false); return; }

    try {
      const at = localStorage.getItem(DISMISS_KEY);
      if (at && (Date.now() - Number(at)) / 86400_000 < DISMISS_DAYS) {
        setShow(false);
        return;
      }
    } catch (_) { /* ignore */ }

    const refresh = () => {
      const installState = getInstallState();
      // On iOS, push only works after Add-to-Home-Screen
      if (installState.isIOS && !installState.installed) {
        setShow(false);
        return;
      }
      setShow(true);
    };
    refresh();

    // Re-evaluate if PWA install state changes mid-session
    const off = onInstallStateChange(refresh);

    // Also check server-side: if subscription already exists, hide
    getPushStatus(token).then((s) => {
      if (s?.isSubscribed) setShow(false);
    }).catch(() => {});

    return off;
  }, [user, token]);

  if (!show || done) return null;

  const onEnable = async () => {
    setBusy(true); setErr('');
    try {
      const res = await subscribeToPush(token);
      if (res.success) {
        setDone(true);
        // Audible confirmation so seller knows it works
        playNotificationSound('cash_register');
      } else {
        setErr(res.message || 'Failed to enable alerts');
      }
    } catch (e) {
      setErr(e.message || 'Failed to enable alerts');
    } finally {
      setBusy(false);
    }
  };

  const onDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    setShow(false);
  };

  return (
    <div
      data-testid="enable-push-banner"
      style={{
        position: 'fixed',
        top: 'max(env(safe-area-inset-top), 12px)',
        left: 16,
        right: 16,
        zIndex: 9000,
        background: 'linear-gradient(135deg, #BA2D1B 0%, #E46B2C 100%)',
        color: '#fff',
        borderRadius: 14,
        padding: '12px 14px',
        boxShadow: '0 10px 30px rgba(186, 45, 27, 0.4)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        maxWidth: 620,
        margin: '0 auto',
        fontFamily: 'inherit',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 36, height: 36, borderRadius: 8,
          background: 'rgba(255,255,255,0.18)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20, flexShrink: 0,
        }}
      >
        🔔
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>
          Never miss an order
        </div>
        <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>
          Get a cash-register ding for every new order, plus alerts for messages and low stock.
        </div>
        {err && (
          <div style={{ fontSize: 12, color: '#fee2e2', marginTop: 4 }}>
            {err}
          </div>
        )}
      </div>
      <button
        data-testid="enable-push-cta"
        onClick={onEnable}
        disabled={busy}
        style={{
          background: 'white',
          color: '#BA2D1B',
          border: 0,
          padding: '9px 14px',
          borderRadius: 10,
          fontWeight: 800,
          fontSize: 13,
          cursor: busy ? 'progress' : 'pointer',
          opacity: busy ? 0.7 : 1,
          flexShrink: 0,
        }}
      >
        {busy ? 'Enabling…' : 'Enable'}
      </button>
      <button
        data-testid="enable-push-dismiss"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: 'transparent',
          color: 'rgba(255,255,255,0.75)',
          border: 0,
          padding: 6,
          cursor: 'pointer',
          fontSize: 18,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}
